<template>
  <div>
    <v-row
      dense
      class="mb-2"
    >
      <v-col
        cols="12"
        sm="6"
        md="5"
      >
        <v-text-field
          v-model="search"
          label="Buscar plano"
          placeholder="Nome ou código..."
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          clearable
          hide-details
          @keyup.enter="reload(true)"
          @click:clear="onClear"
        />
      </v-col>
      <v-col
        cols="12"
        md="2"
        offset-md="5"
        class="d-flex align-center justify-md-end"
      >
        <v-btn
          color="secondary"
          small
          block
          @click="openCreate"
        >
          <v-icon
            left
            small
          >
            mdi-plus
          </v-icon>
          Novo plano
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="items"
      :server-items-length="total"
      :options.sync="options"
      :loading="loading"
      :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      item-key="id"
      dense
    >
      <template #item.valor_assento_profissional="{ item }">
        R$ {{ formatMoney(item.valor_assento_profissional) }}
      </template>
      <template #item.duracao_dias="{ item }">
        {{ item.duracao_dias }} dias
      </template>
      <template #item.actions="{ item }">
        <v-btn
          icon
          small
          @click="openEdit(item)"
        >
          <v-icon small>
            mdi-pencil
          </v-icon>
        </v-btn>
        <v-btn
          icon
          small
          color="error"
          :disabled="item.assinaturas_ativas > 0"
          @click="remove(item)"
        >
          <v-icon small>
            mdi-delete
          </v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog
      v-model="dialog.show"
      max-width="520"
      persistent
    >
      <v-card>
        <v-card-title>{{ dialog.mode === 'create' ? 'Novo plano' : 'Editar plano' }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="dialog.form.codigo_plano"
            label="Código"
            outlined
            dense
          />
          <v-text-field
            v-model="dialog.form.nome"
            label="Nome"
            outlined
            dense
            class="mt-2"
          />
          <v-row dense>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model.number="dialog.form.valor_assento_profissional"
                label="Valor por assento (R$)"
                type="number"
                step="0.01"
                outlined
                dense
              />
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model.number="dialog.form.duracao_dias"
                label="Duração (dias)"
                type="number"
                min="1"
                outlined
                dense
              />
            </v-col>
          </v-row>
          <v-textarea
            v-model="dialog.form.descricao"
            label="Descrição"
            outlined
            dense
            rows="2"
            auto-grow
          />
          <v-divider class="mb-3" />
          <div class="text-subtitle-2 mb-1">
            Assinatura digital
          </div>
          <v-checkbox
            v-model="dialog.form.permite_auto_assinatura"
            label="Permite auto-assinatura"
            hide-details
            dense
            class="mt-0 mb-3"
          />
          <v-divider class="mb-3" />
          <div class="text-subtitle-2 mb-1">
            Recursos do portal do paciente
          </div>
          <v-checkbox
            v-model="dialog.form.permite_portal"
            label="Permite portal do paciente"
            hide-details
            dense
            class="mt-0"
            @change="onPermitePortalChange"
          />
          <div class="pl-6">
            <v-checkbox
              v-model="dialog.form.permite_portal_confirmacao"
              label="Confirmação de agendamentos"
              :disabled="!dialog.form.permite_portal"
              hide-details
              dense
              class="mt-1"
            />
            <v-checkbox
              v-model="dialog.form.permite_portal_agendamento"
              label="Agendamento online"
              :disabled="!dialog.form.permite_portal"
              hide-details
              dense
              class="mt-1"
            />
            <v-checkbox
              v-model="dialog.form.permite_portal_checkin"
              label="Check-in online"
              :disabled="!dialog.form.permite_portal"
              hide-details
              dense
              class="mt-1"
            />
            <v-checkbox
              v-model="dialog.form.permite_portal_agendamento_futuro"
              label="Agendamento de retornos futuros"
              :disabled="!dialog.form.permite_portal"
              hide-details
              dense
              class="mt-1"
            />
          </div>
          <v-alert
            v-if="dialog.error"
            type="error"
            dense
            outlined
            border="left"
          >
            {{ dialog.error }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="dialog.saving"
            @click="dialog.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="secondary"
            :loading="dialog.saving"
            @click="submit"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
  import { $k } from '@/lib/kraken'

  export default {
    name: 'PlansSection',

    data: () => ({
      search: '',
      items: [],
      total: 0,
      options: { page: 1, itemsPerPage: 10 },
      loading: false,
      headers: [
        { text: 'ID', value: 'id', width: 80 },
        { text: 'Código', value: 'codigo_plano', width: 140 },
        { text: 'Nome', value: 'nome' },
        { text: 'Valor (assento)', value: 'valor_assento_profissional', width: 160 },
        { text: 'Duração', value: 'duracao_dias', width: 110 },
        { text: 'Ativas', value: 'assinaturas_ativas', width: 90 },
        { text: '', value: 'actions', width: 110, sortable: false, align: 'end' },
      ],
      dialog: {
        show: false,
        mode: 'create',
        saving: false,
        error: '',
        editingId: null,
        form: {
          codigo_plano: '',
          nome: '',
          valor_assento_profissional: 0,
          duracao_dias: 30,
          descricao: '',
          permite_portal: false,
          permite_portal_confirmacao: false,
          permite_portal_agendamento: false,
          permite_portal_checkin: false,
          permite_portal_agendamento_futuro: false,
          permite_auto_assinatura: false,
        },
      },
      snackbar: { show: false, color: 'success', text: '' },
    }),

    watch: {
      options: {
        deep: true,
        handler () { this.reload() },
      },
    },

    mounted () {
      this.reload(true)
    },

    methods: {
      formatMoney (v) {
        const n = Number(v)
        if (Number.isNaN(n)) return v
        return n.toFixed(2).replace('.', ',')
      },
      msgFromError (err, fallback) {
        return err?.response?.data?.messages?.error?.[0] ||
          err?.response?.data?.messages?.[0]?.text ||
          err?.message ||
          fallback
      },
      onClear () {
        this.search = ''
        this.reload(true)
      },
      async reload (resetPage = false) {
        if (resetPage) this.options = { ...this.options, page: 1 }
        this.loading = true
        try {
          const skip = (this.options.page - 1) * this.options.itemsPerPage
          const payload = { skip, limit: this.options.itemsPerPage }
          if (this.search) payload.q = this.search
          const res = await $k.manage.clinic_one.plans.get(payload)
          this.items = res.results || []
          this.total = res.total || 0
        } catch (err) {
          console.error('[plans.get]', err)
        } finally {
          this.loading = false
        }
      },
      openCreate () {
        this.dialog = {
          show: true,
          mode: 'create',
          saving: false,
          error: '',
          editingId: null,
          form: {
            codigo_plano: '',
            nome: '',
            valor_assento_profissional: 0,
            duracao_dias: 30,
            descricao: '',
            permite_portal: false,
            permite_portal_confirmacao: false,
            permite_portal_agendamento: false,
            permite_portal_checkin: false,
            permite_portal_agendamento_futuro: false,
            permite_auto_assinatura: false,
          },
        }
      },
      openEdit (item) {
        this.dialog = {
          show: true,
          mode: 'edit',
          saving: false,
          error: '',
          editingId: item.id,
          form: {
            codigo_plano: item.codigo_plano,
            nome: item.nome,
            valor_assento_profissional: Number(item.valor_assento_profissional),
            duracao_dias: item.duracao_dias,
            descricao: item.descricao || '',
            permite_portal: !!item.permite_portal,
            permite_portal_confirmacao: !!item.permite_portal_confirmacao,
            permite_portal_agendamento: !!item.permite_portal_agendamento,
            permite_portal_checkin: !!item.permite_portal_checkin,
            permite_portal_agendamento_futuro: !!item.permite_portal_agendamento_futuro,
            permite_auto_assinatura: !!item.permite_auto_assinatura,
          },
        }
      },
      onPermitePortalChange (value) {
        if (!value) {
          this.dialog.form.permite_portal_confirmacao = false
          this.dialog.form.permite_portal_agendamento = false
          this.dialog.form.permite_portal_checkin = false
          this.dialog.form.permite_portal_agendamento_futuro = false
        }
      },
      async submit () {
        const { mode, form, editingId } = this.dialog
        if (!form.codigo_plano?.trim() || !form.nome?.trim()) {
          this.dialog.error = 'Código e nome são obrigatórios.'
          return
        }
        this.dialog.saving = true
        this.dialog.error = ''
        try {
          const body = {
            codigo_plano: form.codigo_plano.trim(),
            nome: form.nome.trim(),
            valor_assento_profissional: Number(form.valor_assento_profissional) || 0,
            duracao_dias: Number(form.duracao_dias) || 30,
            descricao: form.descricao?.trim() || null,
            permite_portal: !!form.permite_portal,
            permite_portal_confirmacao: !!form.permite_portal && !!form.permite_portal_confirmacao,
            permite_portal_agendamento: !!form.permite_portal && !!form.permite_portal_agendamento,
            permite_portal_checkin: !!form.permite_portal && !!form.permite_portal_checkin,
            permite_portal_agendamento_futuro: !!form.permite_portal && !!form.permite_portal_agendamento_futuro,
            permite_auto_assinatura: !!form.permite_auto_assinatura,
          }
          if (mode === 'create') {
            await $k.manage.clinic_one.plans.post(body)
          } else {
            await $k.manage.clinic_one.plans._id.put({ id: editingId, ...body })
          }
          this.snackbar = { show: true, color: 'success', text: 'Plano salvo.' }
          this.dialog.show = false
          await this.reload()
        } catch (err) {
          console.error('[plans.submit]', err)
          this.dialog.error = this.msgFromError(err, 'Falha ao salvar.')
        } finally {
          this.dialog.saving = false
        }
      },
      async remove (item) {
        if (!confirm(`Excluir o plano "${item.nome}"?`)) return
        try {
          await $k.manage.clinic_one.plans._id.delete({ id: item.id })
          this.snackbar = { show: true, color: 'success', text: 'Plano excluído.' }
          await this.reload()
        } catch (err) {
          console.error('[plans.delete]', err)
          this.snackbar = { show: true, color: 'error', text: this.msgFromError(err, 'Falha ao excluir.') }
        }
      },
    },
  }
</script>
